// END-CALL: every way a Director call ends, in one place, and the guarantee that none of them keeps the microphone.
//
// The ways a call ends: the user's End / X / light (`user`), the Director hanging up with ElevenLabs' end_call system
// tool (`agent`), three quiet minutes (`idle`, idleController.ts), the call's time or credit limit (`limit`), signing
// out (`signout`), and the page going away or sitting in the background on a phone (`hidden`, watchPage below).
//
// The SDK's endSession closes the connection, and that stops its mic track (LiveKit's disconnect, or input.close ->
// track.stop on the WebSocket path). MicGuard is the belt and braces under it: while a call is open it notes every
// stream getUserMedia hands out, and when the call ends it stops their tracks itself, including a stream whose
// permission prompt was answered after the call had already ended. Stopping a stopped track does nothing, so it never
// fights the SDK. Streams asked for while no call is open are none of its business.
//
// Pure apart from what's passed in, so callend.test.ts drives every end path with a fake MediaStream.

export type EndReason = "user" | "agent" | "idle" | "limit" | "signout" | "hidden";
/** What the UI says after a call ended without the user's own tap (store `directorEnded`). */
export type EndedState = "idle" | "agent" | "hidden";
/** The SDK's onDisconnect details (@elevenlabs/client DisconnectionDetails), the parts we read. */
export type Disconnect = { reason: string; context?: { type?: string; reason?: string } };

// ------------------------------------------------------------------ the microphone

export type TrackLike = { stop(): void; readonly readyState?: string };
export type StreamLike = { getTracks(): TrackLike[] };
export type MediaDevicesLike = { getUserMedia(constraints?: MediaStreamConstraints): Promise<StreamLike> };

const WRAPPED = Symbol.for("cao.micguard");

export class MicGuard {
  private seq = 0;
  /** the open call's number (0: none) */
  private call = 0;
  private streams = new Set<StreamLike>();

  /** Wrap `md.getUserMedia` (once) so streams handed out during a call are noted. */
  install(md: MediaDevicesLike | undefined | null): void {
    if (!md || typeof md.getUserMedia !== "function" || (md as unknown as Record<symbol, unknown>)[WRAPPED]) return;
    const original = md.getUserMedia.bind(md);
    const guarded = async (constraints?: MediaStreamConstraints) => {
      const asked = this.call;
      const s = await original(constraints);
      if (asked) {
        // the call ended while the prompt was up: nobody will use this stream
        if (this.call !== asked) stopTracks(s);
        else this.streams.add(s);
      }
      return s;
    };
    (guarded as unknown as Record<symbol, unknown>)[WRAPPED] = true;
    md.getUserMedia = guarded as MediaDevicesLike["getUserMedia"];
    (md as unknown as Record<symbol, unknown>)[WRAPPED] = true;
  }

  /** A call is starting: from now on, getUserMedia streams belong to it. */
  begin(): void {
    this.release();
    this.call = ++this.seq;
  }

  /** The call is over: stop every track it got. Returns how many were still live. Safe to call any number of times. */
  release(): number {
    let n = 0;
    for (const s of this.streams) n += stopTracks(s);
    this.streams.clear();
    this.call = 0;
    return n;
  }

  /** Tracks of this call's streams that are still live (tests, and a dev check). */
  liveTracks(): number {
    let n = 0;
    for (const s of this.streams) for (const t of s.getTracks()) if (t.readyState !== "ended") n++;
    return n;
  }

  get open(): boolean {
    return this.call !== 0;
  }
}

function stopTracks(s: StreamLike): number {
  let n = 0;
  for (const t of s.getTracks()) {
    if (t.readyState !== "ended") n++;
    try {
      t.stop();
    } catch {
      /* already gone */
    }
  }
  return n;
}

// ------------------------------------------------------------------ the call's end, one state machine

export type DisconnectKind = "hangup" | "dropped" | "ended" | "error";
export type DisconnectResult = {
  kind: DisconnectKind;
  /** why it ended, as far as the app knows */
  reason: EndReason | "error" | "dropped";
  /** what the UI says ("Call ended" and why), or null for a plain End tap */
  ended: EndedState | null;
  /** a dropped line (ElevenLabs closed it, not end_call) may reconnect quietly; nothing else does */
  mayReconnect: boolean;
};

/**
 * The bridge's end state: who asked for the end, whether the Director hung up, and the mic. directorBridge.tsx calls
 * `starting` before startSession, `stop(reason)` for every app-side end, `agentTool` from onAgentToolRequest /
 * onAgentToolResponse, and `disconnected` from onDisconnect.
 */
export class CallLine {
  private stopReason: EndReason | null = null;
  private hungUp = false;

  constructor(
    readonly mic: MicGuard,
    private endSession: () => void,
  ) {}

  /** A new call is being opened (also after a quiet reconnect or a voice switch). */
  starting(): void {
    this.stopReason = null;
    this.hungUp = false;
    this.mic.begin();
  }

  /** The app ends the call (End tap, idle, limit, sign-out, page hidden). */
  stop(reason: EndReason = "user"): void {
    if (!this.stopReason) this.stopReason = reason;
    try {
      this.endSession();
    } finally {
      // even if the SDK had nothing to end (still connecting) or threw, the mic is let go now
      this.mic.release();
    }
  }

  /** Whether the app (not the Director or the network) asked for this end. */
  get stopped(): boolean {
    return this.stopReason !== null;
  }

  /** ElevenLabs reported a system tool call: end_call means the Director is hanging up. */
  agentTool(name: string | undefined): void {
    if (name === "end_call") this.hungUp = true;
  }

  /** onDisconnect: what happened, and the mic is released whatever it was. */
  disconnected(d: Disconnect): DisconnectResult {
    this.mic.release();
    const by = this.stopReason;
    const hungUp = this.hungUp || (d.reason === "agent" && d.context?.type === "end_call");
    this.stopReason = null;
    this.hungUp = false;
    if (by) return { kind: "ended", reason: by, ended: by === "idle" || by === "hidden" ? by : null, mayReconnect: false };
    if (hungUp) return { kind: "hangup", reason: "agent", ended: "agent", mayReconnect: false };
    if (d.reason === "agent") return { kind: "dropped", reason: "dropped", ended: null, mayReconnect: true };
    if (d.reason === "error") return { kind: "error", reason: "error", ended: null, mayReconnect: false };
    return { kind: "ended", reason: "user", ended: null, mayReconnect: false };
  }
}

// ------------------------------------------------------------------ what the screen says after

/** The line under "Call ended" for an end the user didn't tap (idle keeps its own copy in Talk / Director). */
export function hangupLine(ended: EndedState | null, playing: boolean): string | null {
  if (ended === "agent") return playing ? "Your Director hung up. The video keeps playing. Tap to call back." : "Your Director hung up. Tap to call back any time.";
  if (ended === "hidden") return "It ended when you left the app. Tap to pick up where you left off.";
  return null;
}

// ------------------------------------------------------------------ the page going away

/**
 * How long a phone may sit in the background with a call open before the app ends it. Long enough to glance at a
 * message and come back; short enough that a forgotten call doesn't hold the mic (and bill minutes).
 */
export const HIDDEN_GRACE_MS = 30_000;

export type PageDeps = {
  /** a call is open (connecting or connected) */
  live: () => boolean;
  /** media is playing right now (a long video the user may be listening to) */
  playing: () => boolean;
  /** a phone or tablet (the background rule applies there; desktop tabs keep their call in the background) */
  mobile: () => boolean;
  hidden: () => boolean;
  end: (reason: "hidden") => void;
  now: () => number;
  setTimer: (f: () => void, ms: number) => unknown;
  clearTimer: (t: unknown) => void;
};

/**
 * The page lifecycle rule:
 * - `pagehide` (tab closed, navigated away, or put in the back-forward cache) and `freeze` (Chrome on Android
 *   discarding a background tab) end a live call at once, on every device: the page can't hold a call it can't show.
 * - `visibilitychange` to hidden on a phone starts a HIDDEN_GRACE_MS timer; still hidden then, and nothing playing,
 *   the call ends. If something is playing it checks again a grace later. iOS Safari doesn't fire `pagehide` when the
 *   user switches apps or tabs, and freezes background timers, so visibilitychange is the signal there; coming back
 *   after the grace (a frozen timer never ran) ends it too, unless media is playing.
 */
export function pageWatch(d: PageDeps) {
  let hiddenAt: number | null = null;
  let timer: unknown = null;
  const clear = () => {
    if (timer !== null) d.clearTimer(timer);
    timer = null;
  };
  const check = () => {
    timer = null;
    if (!d.hidden() || !d.live()) return;
    if (d.playing()) {
      timer = d.setTimer(check, HIDDEN_GRACE_MS);
      return;
    }
    hiddenAt = null;
    d.end("hidden");
  };
  return {
    pagehide() {
      clear();
      hiddenAt = null;
      if (d.live()) d.end("hidden");
    },
    freeze() {
      clear();
      hiddenAt = null;
      if (d.live()) d.end("hidden");
    },
    visibility() {
      if (d.hidden()) {
        if (!d.mobile() || hiddenAt !== null || !d.live()) return;
        hiddenAt = d.now();
        timer = d.setTimer(check, HIDDEN_GRACE_MS);
        return;
      }
      const was = hiddenAt;
      hiddenAt = null;
      clear();
      if (was !== null && d.now() - was >= HIDDEN_GRACE_MS && d.live() && !d.playing()) d.end("hidden");
    },
  };
}

/** Phones and tablets, including iPadOS (which says it's a Mac but has touch). */
export function isMobileDevice(nav: { userAgent?: string; maxTouchPoints?: number } | undefined, coarse: boolean): boolean {
  const ua = nav?.userAgent ?? "";
  if (/iPhone|iPad|iPod|Android|Mobile/i.test(ua)) return true;
  if (/Macintosh/.test(ua) && (nav?.maxTouchPoints ?? 0) > 1) return true;
  return coarse;
}

/** Wire pageWatch to the window. Returns a function that removes the listeners. */
export function watchPage(deps: Omit<PageDeps, "hidden" | "now" | "setTimer" | "clearTimer" | "mobile">): () => void {
  if (typeof window === "undefined" || typeof document === "undefined") return () => {};
  const coarse = () => {
    try {
      return window.matchMedia?.("(pointer: coarse)").matches ?? false;
    } catch {
      return false;
    }
  };
  const w = pageWatch({
    ...deps,
    mobile: () => isMobileDevice(navigator, coarse()),
    hidden: () => document.visibilityState === "hidden",
    now: () => Date.now(),
    setTimer: (f, ms) => window.setTimeout(f, ms),
    clearTimer: (t) => window.clearTimeout(t as number),
  });
  const onHide = () => w.pagehide();
  const onFreeze = () => w.freeze();
  const onVis = () => w.visibility();
  window.addEventListener("pagehide", onHide);
  document.addEventListener("freeze", onFreeze);
  document.addEventListener("visibilitychange", onVis);
  return () => {
    window.removeEventListener("pagehide", onHide);
    document.removeEventListener("freeze", onFreeze);
    document.removeEventListener("visibilitychange", onVis);
  };
}
